import { useState } from 'react'
import { Navigate } from '@tanstack/react-router'
import { FlightMap } from './FlightMap'
import type { FlightMapMode } from '@/lib/native/flight-map'
import { TopBarHome } from './topbar'
import { HomeFlightDrawer } from './HomeFlightDrawer'
import { DesktopHome } from './DesktopHome'
import {
  useTripGetMyNextFlight,
  useQuickStartProgress,
} from '../lib/hooks'
import { useAirportByIcao } from '../lib/hooks/airport'
import { useCurrentCrewMember } from '../hooks/useCurrentCrewMember'
import { useCurrentDeviceLocation } from '@/hooks/useCurrentDeviceLocation'
import { useIsAdmin } from '../hooks/useIsCompanyAdmin'
import { useIsDesktop } from './ui/use-mobile'

export function Home() {
  const { data: currentUser } = useCurrentCrewMember()
  const { data: quickStartProgress } = useQuickStartProgress()
  const { isAdmin } = useIsAdmin()
  const isDesktop = useIsDesktop()
  const [mapMode, setMapMode] = useState<FlightMapMode>('globe')

  // Query regardless of includeInSchedule — excluded crew can still hold flight
  // assignments, and HomeFlightDrawer's card shows them; the map must match.
  const { data: nextFlight, isLoading: isNextFlightLoading } =
    useTripGetMyNextFlight(currentUser?.id)
  const departureAirport = useAirportByIcao(nextFlight?.departureAirport)
  const arrivalAirport = useAirportByIcao(nextFlight?.arrivalAirport)

  const noFlight = !isNextFlightLoading && !nextFlight && !!currentUser
  const { data: deviceLocation } = useCurrentDeviceLocation(noFlight)

  if (isAdmin && quickStartProgress && !quickStartProgress.ownerOnboardingComplete) {
    return <Navigate to="/operations/onboarding" />
  }

  const getUserInitials = () => {
    if (!currentUser) return 'U'
    if (currentUser.firstName && currentUser.lastName) {
      return `${currentUser.firstName.charAt(0)}${currentUser.lastName.charAt(0)}`.toUpperCase()
    }
    if (currentUser.name) {
      const nameParts = currentUser.name.trim().split(' ')
      if (nameParts.length >= 2) {
        return `${nameParts[0].charAt(0)}${nameParts[nameParts.length - 1].charAt(0)}`.toUpperCase()
      }
      return nameParts[0].charAt(0).toUpperCase()
    }
    return 'U'
  }

  const getUserFirstName = () => {
    if (!currentUser) return 'User'
    if (currentUser.firstName) return currentUser.firstName
    if (currentUser.name) return currentUser.name.trim().split(' ')[0]
    return 'User'
  }

  // Desktop web: interactive Apple MapKit JS map background with the Figma
  // content column (flight header + cards) floating over it. DesktopHome owns
  // its own data hooks; mobile/native keeps the bottom-drawer layout below.
  if (isDesktop) {
    return <DesktopHome />
  }

  return (
    <>
      {/* Full-viewport native map behind everything. The map's body/#root
          transparency hack lets the MKMapView render through. */}
      <div className="fixed inset-0 z-0">
        <FlightMap
          departure={departureAirport ?? undefined}
          arrival={arrivalAirport ?? undefined}
          departureDateTime={nextFlight?.departureDateTime}
          arrivalDateTime={nextFlight?.arrivalDateTime}
          location={
            noFlight && deviceLocation
              ? {
                  latitude: deviceLocation.latitude,
                  longitude: deviceLocation.longitude,
                  label: deviceLocation.cityName,
                }
              : undefined
          }
          showModeToggle={false}
          mode={mapMode}
          interactive={true}
          cameraAltitude={noFlight ? 25_000_000 : 50_000_000}
          edgePadding={
            {
                  top: 96,
                  left: 40,
                  bottom: Math.round(window.innerHeight * 0.7),
                  right: 40,
                }
          }
        />
      </div>

      <div className="fixed inset-x-0 top-0 z-10">
        <TopBarHome
          userName={getUserFirstName()}
          userInitials={getUserInitials()}
          avatarColor={currentUser?.avatarColor}
          transparent
          mapMode={mapMode}
          onToggleMapMode={() =>
            setMapMode((m) => (m === 'globe' ? 'flat' : 'globe'))
          }
        />
      </div>

      <HomeFlightDrawer />
    </>
  )
}
